namespace Portico.Services {
    import IAccessToken = Portico.Models.Interfaces.IAccessToken;
    import ResponseModel = Portico.Models.Enterprise.ResponseModel;
    import IResponseModel = Portico.Models.Interfaces.IResponseModel;
    import ILegacyExternalResponseModel = Portico.Models.Interfaces.ILegacyExternalResponseModel;
    import IServiceDefinition = Portico.Models.Interfaces.IServiceDefinition;
    import IServiceUrl = Portico.Models.Interfaces.IServiceUrl;

    function delegate<T, TClass>(ctor: { new(model: T): TClass; } | string) {
		return (item: angular.IHttpPromiseCallbackArg<any>) =>
			ctor == null ? item.data as T : _.isString(ctor) ? item.data as T : new ctor(item.data);
    }

    interface HttpMethodWithData {
        <T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        <T extends ILegacyExternalResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        <T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        <T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        <T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        <T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        <T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
    }

    interface HttpMethodWithoutData {
        <T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        <T extends ILegacyExternalResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        <T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        <T extends IResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        <T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<never>;
        <T extends IResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<never>;
        <T>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
    }

    export interface IServiceInstance {
        get: HttpMethodWithoutData;
        GET: HttpMethodWithoutData;

        post: HttpMethodWithData;
        POST: HttpMethodWithData;

        put: HttpMethodWithData;
        PUT: HttpMethodWithData;

        delete: HttpMethodWithData;
        DELETE: HttpMethodWithData;
    }

    export class ServiceInstance<TSettings extends IServiceUrl> implements IServiceInstance {
        private spinner: string;
        private className = 'Portico.Services.ServiceInstance';

        constructor(
            protected serviceUrl: TSettings,
            protected $http: ng.IHttpService,
            protected $q: ng.IQService,
            protected dataService: Portico.Services.DataService,
            private modalService: Portico.Services.IModalService,
            private errorService: Portico.Services.IErrorService,
            private $rootScope: ng.IRootScopeService,
            private requestToken: (serviceUrl: TSettings) => ng.IPromise<string>,
            private getHttpConfig: (serviceUrl: TSettings, token: string) => ng.IRequestConfig
        ) {
            this.$http.defaults.headers.common['accept'] = 'application/json';
            this.$http.defaults.headers.common["X-Requested-With"] = 'XMLHttpRequest';

            this.spinner = "spinner";

            if (this.serviceUrl.url.substr(-1) === '/') {
                this.serviceUrl.url = this.serviceUrl.url.substr(0, this.serviceUrl.url.length - 1);
            }
        }

        public get<T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        public get<T extends ILegacyExternalResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        public get<T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        public get<T extends IResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        public get<T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public get<T extends IResponseModel<any>>(url: string, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public get<T>(url: string, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
        public get<T, TClass, TResult>(ctor: { new(model: T): TClass; } | string, url?: string | { [key: string]: string; }, headers?: { [key: string]: string; }): ng.IPromise<TResult> {
            if (_.isString(ctor)) {
                headers = <any>url;
                url = ctor;
                ctor = null;
            }

            this.showSpinner();

            return this.$get<T>(<any>url, headers)
                .then(delegate(ctor))
                .then(resp => {
                    var model = new ResponseModel<TResult>(<any>resp);

                    if (!model.success)
                        return <any>this.$q.reject(model.messages);

                    return model.data;
                })
                .finally(() => {
                    this.hideSpinner();
                });
        }

        public GET: typeof ServiceInstance.prototype.get;

        public post<T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        public post<T extends ILegacyExternalResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        public post<T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        public post<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        public post<T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public post<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public post<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
        public post<T, TClass, TResult>(ctor: { new(model: T): TClass; } | string, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<any> {
            if (_.isString(ctor)) {
                headers = <any>data;
                data = url;
                url = ctor;
                ctor = null;
            }

            this.showSpinner();

            return this.$post<typeof data>(url, data, headers)
                .then(delegate(ctor))
                .then(resp => {
                    var model = new ResponseModel<TResult>(resp);

                    if (!model.success)
                        return <any>this.$q.reject(model.messages);

                    return model.data;
                })
                .finally(() => {
                    this.hideSpinner();
                });
        }

        public POST: typeof ServiceInstance.prototype.post;

        public put<T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        public put<T extends ILegacyExternalResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        public put<T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        public put<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        public put<T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public put<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public put<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
        public put<T, TClass, TResult>(ctor: { new(model: T): TClass; } | string, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TResult> {
            if (_.isString(ctor)) {
                headers = <any>data;
                data = url;
                url = ctor;
                ctor = null;
            }

            this.showSpinner();

            return this.$put<typeof data>(url, data, headers)
                .then(delegate(ctor))
                .then(resp => {
                    var model = new ResponseModel<TResult>(resp);

                    if (!model.success)
                        return <any>this.$q.reject(model.messages);

                    return model.data;
                })
                .finally(() => {
                    this.hideSpinner();
                });
        }

        public PUT: typeof ServiceInstance.prototype.put;

        public delete<T extends ILegacyExternalResponseModel<any>, TClass extends { data: { results: any; } }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']['results']>;
        public delete<T extends ILegacyExternalResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']['results']>;
        public delete<T extends IResponseModel<any>, TClass extends { data: any }>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TClass['data']>;
        public delete<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T['data']>;
        public delete<T extends IResponseModel<any>, TClass>(ctor: { new(model: T): TClass; }, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public delete<T extends IResponseModel<any>>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<never>;
        public delete<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<T>; // NOTE: This overload might throw in new ResponseModel<>
        public delete<T, TClass, TResult>(ctor: { new(model: T): TClass; } | string, url: string, data?: any, headers?: { [key: string]: string; }): ng.IPromise<TResult> {
            if (_.isString(ctor)) {
                headers = <any>data;
                data = url;
                url = ctor;
                ctor = null;
            }

            this.showSpinner();

            return this.$delete<typeof data>(url, data, headers)
                .then(delegate(ctor))
                .then(resp => {
                    var model = new ResponseModel<TResult>(resp);

                    if (!model.success)
                        return <any>this.$q.reject(model.messages);

                    return model.data;
                })
                .finally(() => {
                    this.hideSpinner();
                });
        }

        public DELETE: typeof ServiceInstance.prototype.delete;

        private normalizeUrl(url: string): string {
            if (url.substr(0, 1) !== '/') {
                return `/${url}`;
            }

            return url;
        }

        private $get<T>(url: string, headers?: { [key: string]: string; }): ng.IHttpPromise<T> {
            url = this.normalizeUrl(url);

            return this.requestToken(this.serviceUrl)
                .then((token: string) => {
                    return this.$q.when(this.getHttpConfig(this.serviceUrl, token));
                })
                .then((config: ng.IRequestConfig) => {
                    config.headers = angular.merge(config.headers, headers);
                    return this.$http.get<T>(this.serviceUrl.url + url, config);
                })
                .catch((error: any) => {
                    return this.handleError(error, url, `${this.className}.$get`);
                });
        }

        private $post<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IHttpPromise<T> {
            url = this.normalizeUrl(url);

            return this.requestToken(this.serviceUrl)
                .then((token: string) => {
                    return this.$q.when(this.getHttpConfig(this.serviceUrl, token));
                })
                .then((config: ng.IRequestConfig) => {
                    config.headers = angular.merge(config.headers, headers);
                    return this.$http.post<T>(this.serviceUrl.url + url, data, config);
                })
                .catch((error: any) => {
                    return this.handleError(error, url, `${this.className}.$post`, data);
                });
        }

        private $put<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IHttpPromise<T> {
            url = this.normalizeUrl(url);

            return this.requestToken(this.serviceUrl)
                .then((token: string) => {
                    return this.$q.when(this.getHttpConfig(this.serviceUrl, token));
                })
                .then((config: ng.IRequestConfig) => {
                    config.headers = angular.merge(config.headers, headers);
                    return this.$http.put<T>(this.serviceUrl.url + url, data, config);
                })
                .catch((error: any) => {
                    return this.handleError(error, url, `${this.className}.$put`, data);
                });
        }

        private $delete<T>(url: string, data?: any, headers?: { [key: string]: string; }): ng.IHttpPromise<T> {
            url = this.normalizeUrl(url);

            return this.requestToken(this.serviceUrl)
                .then((token: string) => {
                    return this.$q.when(this.getHttpConfig(this.serviceUrl, token));
                })
                .then((config: ng.IRequestConfig) => {

                    if (data !== undefined && data !== null) {
                        config.data = data;
                        config.headers['Content-Type'] = 'application/json;charset=utf-8';
                    }

                    config.headers = angular.merge(config.headers, headers);
                    return this.$http.delete<T>(this.serviceUrl.url + url, config);
                })
                .catch((error: any) => {
                    return this.handleError(error, url, `${this.className}.$delete`, data);
                });
        }

        private handleError(error: any, url: string, methodName: string, data?: any): ng.IPromise<any> {
            this.logError(error, url, methodName, data);
            return this.$q.reject(error);
        }

        private logError(error: any, url: string, methodName: string, data?: any): void {
            let dataDump = null;

            if (data) {
                dataDump = JSON.stringify(data);
            }

            let rawError = (typeof error) === 'object' ? JSON.stringify(error) : error;

            this.errorService.handleError(new Portico.Models.PorticoException(`[${methodName}]`, rawError, 0, dataDump));
        }

        private showSpinner(): void {
            if (_.get(this.$rootScope, 'spinnerDisabled', false) === false) {
                // this.modalService.show(this.spinner);
                document.getElementById('global-spinner-overlay').style.display = 'flex';
            }
        }

        private hideSpinner(): void {
            // this.modalService.hide(this.spinner);
            document.getElementById('global-spinner-overlay').style.display = 'none';
        }
    }
    (<any>ServiceInstance).prototype.GET = (<any>ServiceInstance).prototype.get;
    (<any>ServiceInstance).prototype.POST = (<any>ServiceInstance).prototype.post;
    (<any>ServiceInstance).prototype.PUT = (<any>ServiceInstance).prototype.put;
    (<any>ServiceInstance).prototype.DELETE = (<any>ServiceInstance).prototype.delete;
}